import IBase from './base.model';
import IApp from './app.model';
import IMember from './member.model';
import IPlan from './plan.model';

export enum Role {
  owner = 'OWNER',
  admin = 'ADMIN',
  developer = 'DEVELOPER'
}
export enum AuthKeyType {
  SYMBOLS = 'symbols',
  API = 'api',
  MCP = 'mcp'
}


export interface IAuthKey{
  type: AuthKeyType;
  key: string;
  name: string;
  expDate: Date;
}

export interface IMemberRole {
  member: IMember | string;
  role: Role;
}

export interface IAddress {
  country: string;
  state: string;
  address: string;
  city: string;
  zip: string;
  label: string;
}

export interface IBilling {
  vaultedShopperId: string;
  subscriptionId: string;
}

export interface IInvoice {
  id: string,
  number: number;
  url: {
      origin: string,
      he: string,
      en: string
  }
}

export enum PaymentMethod {
  CREDIT_CARD = 'CREDIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER'
}

export interface IPayment {
  date: Date;
  amount: number;
  plan: IPlan | string;
  invoice?: IInvoice;
  paymentMethod?: PaymentMethod;
  month?: number;
  startDate?: Date;
}

export enum Percent {
  EIGHTY = 80, 
  HUNDRED = 100
}

export interface INotification {
  msg: string;
  url: string;
  linkMsg: string;
}

export enum HeardAbout {
  GoogleSearch = 'google_search',
  ChatGPT = 'chatgpt',
  Claude = 'claude',
  Perplexity = 'perplexity',
  Gemini = 'gemini',
  Copilot = 'copilot',
  OtherAI = 'other_ai',
  YouTube = 'youtube',
  Reddit = 'reddit',
  LinkedIn = 'linkedin',
  X = 'x',
  GitHub = 'github',
  Article = 'article',
  Colleague = 'colleague',
  Other = 'other'
}

export const heardAboutLabels: Record<HeardAbout, string> = {
  [HeardAbout.GoogleSearch]: 'Google search',
  [HeardAbout.ChatGPT]: 'ChatGPT',
  [HeardAbout.Claude]: 'Claude',
  [HeardAbout.Perplexity]: 'Perplexity',
  [HeardAbout.Gemini]: 'Gemini',
  [HeardAbout.Copilot]: 'Copilot',
  [HeardAbout.OtherAI]: 'Another AI assistant',
  [HeardAbout.YouTube]: 'YouTube',
  [HeardAbout.Reddit]: 'Reddit',
  [HeardAbout.LinkedIn]: 'LinkedIn',
  [HeardAbout.X]: 'X (Twitter)',
  [HeardAbout.GitHub]: 'GitHub',
  [HeardAbout.Article]: 'An article or blog post',
  [HeardAbout.Colleague]: 'A colleague or friend',
  [HeardAbout.Other]: 'Other'
};

export default interface IAccount extends IBase {
  name: string;
  companyName?: string;
  taxId?: string;
  address?: IAddress;
  members: IMemberRole[];
  apps: (IApp)[];
  phone: string;
  heardAbout?: HeardAbout;
  subscription: {
    plan: IPlan | string,
    nextPayment?: Date,
    notification: {
      trialEnd?: number;
      logLimit?: {
        date: Date;
        percent: Percent;  
      },
      failedPayment?: {
        date: Date;
        count: number;
        reason: string;
      }
    },
    exceedLogLimit: Date[];
  };
  billing?: IBilling;
  invoiceEmails?: string[];
  payments: IPayment[];
  authKeys?: IAuthKey[];
  slackIntegration: ISlackIntegration[];
  webhookIntegration?: IWebhookIntegration[];
  githubIntegration?: IGithubIntegration;
  notification?: INotification;
}

export interface IGithubRepoMapping {
  app: string;
  owner: string;
  repo: string;
  branch: string;
}

export interface IGithubIntegration {
  installationId: number;
  accountLogin: string;
  accountType?: string;
  createdAt?: Date;
  repoMappings: IGithubRepoMapping[];
}

export interface IWebhookIntegration {
  _id: string;
  url: string;
  secret?: string;
  headers?: Record<string, string>;
  apps?: string[];
  enabled: boolean;
}

export interface IWebhookIntegrationData {
  url: string;
  headers?: Record<string, string>;
  apps?: string[];
  enabled?: boolean;
}

export enum WebhookEventType {
  Custom = 'custom',
  Automatic = 'automatic',
}

export interface ISlackIntegration {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _id: any,
  slack: {
    app_id: string,
    authed_user: {
      id: string,
      scope?: string,
      access_token?: string,
      token_type?: string,
    },
    scope: string,
    token_type: string,
    access_token: string,
    bot_user_id: string,
    team: {
      id: string,
      name: string
    },
    enterprise?: {
      id: string,
      name: string
    },
    incoming_webhook: {
      channel: string,
      channel_id: string,
      configuration_url: string,
      url: string
    }
  },
  app?: string;
}